import {
  getVeevaVaultAccounts,
  getVeevaVaultLookupItems,
  getVeevaVaultObjectMetadataLayouts,
  getVeevaVaultPicklistsItems,
  getVeevaVaultQueryResults,
} from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { VeevaVaultObjectMetadataLayouts } from '@alucio/aws-beacon-amplify/src/models';
import {
  deleteVeevaVaultObjects,
  endVeevaVaultSession,
  keepVeevaVaultSessionAlive,
  upsertVeevaVaultObjects,
} from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { generateClient } from 'aws-amplify/api';
import { CRMIntegrationSession, CRMUtil } from 'src/state/machines/CRM/util';
import { SYNC_ENTRY_FETCH_TYPE, SYNC_ENTRY_STATUS, SyncEntry } from '../../ISyncManifest';
import {
  getAdditionalEntriesBasedOnLayouts,
  getErrorMessagesFromFailedRequest,
  getStructureObjectLayouts,
  processAccountsResponse,
  structureDeleteObjectsResponse,
  structureUpsertResponse,
} from './VeevaVaultSyncerUtils';
import * as logger from 'src/utils/logger';
import {
  Config,
  CRMAccount,
  CRMSubmitResponseReferences,
  RecordSubmitResponse,
  SYNC_STATUS,
  TableEnum,
} from '../../CRMIndexedDBTypes';
import { Singleton as IndexDbCrm } from '../../CRMIndexedDB';
import {
  VeevaVaultDeleteObjectsOutput,
  VeevaVaultObjectToDeletePayload,
  VeevaVaultUpsertObjectPayload,
  VeevaVaultUpsertObjectsOutput,
} from '../../CRMVeevaVaultTypes';
const CRMDB = IndexDbCrm;

export type VeevaVaultAccountsPage = {
  accounts: CRMAccount[];
  nextPage?: string;
  total: number;
};

export class VeevaVaultAPIService {
  appsyncClient: ReturnType<typeof generateClient>;

  constructor() {
    this.appsyncClient = generateClient();
    this.upsertVeevaVaultObjects = this.upsertVeevaVaultObjects.bind(this);
    this.deleteVeevaVaultObjects = this.deleteVeevaVaultObjects.bind(this);
  }

  private getEncryptedSessionId(): string {
    return CRMUtil.getAuthInformation()?.encryptedSessionId || '';
  }

  async endSession(crmSession?: CRMIntegrationSession): Promise<void> {
    logger.veevaVaultSyncer.debug('Ending Veeva Vault session...');

    try {
      await this.appsyncClient.graphql({
        query: endVeevaVaultSession,
        variables: {
          encryptedSessionId: crmSession?.encryptedSessionId || '',
        },
      });
    } catch (e) {
      logger.veevaVaultSyncer.error('An error occurred when ending session: ', e);
    }
  }

  async keepSessionAlive(): Promise<void> {
    logger.veevaVaultSyncer.debug('Keeping Veeva Vault session alive...');
    await this.appsyncClient.graphql({
      query: keepVeevaVaultSessionAlive,
      variables: {
        encryptedSessionId: this.getEncryptedSessionId(),
      },
    });
  }

  async getCRMAccountsPage(nextPage?: string): Promise<VeevaVaultAccountsPage> {
    logger.veevaVaultSyncer.debug(`Getting ${nextPage ? 'next page of ' : ''}CRM Accounts...`);
    const crmUserId = CRMUtil.getAuthInformation()?.userInfo?.id;
    const configItem = await CRMDB.getById<Config>(TableEnum.CONFIG_OBJECT, crmUserId);
    const { data, errors } = await this.appsyncClient.graphql({
      query: getVeevaVaultAccounts,
      variables: {
        encryptedSessionId: this.getEncryptedSessionId(),
        nextPage,
        lastSyncTime: configItem?.lastSyncTime || 0,
      },
    });

    if (errors || !data?.getVeevaVaultAccounts?.data) {
      throw new Error('Unable to fetch Veeva Vault accounts');
    }

    return {
      accounts: processAccountsResponse(JSON.parse(data.getVeevaVaultAccounts.data)),
      nextPage: data.getVeevaVaultAccounts.nextPage || undefined,
      total: data.getVeevaVaultAccounts.total,
    };
  }

  async getObjectLayouts(syncEntry: SyncEntry): Promise<SyncEntry[]> {
    const objectName = syncEntry.parameters.apiName;

    if (!objectName) {
      logger.veevaVaultSyncer.error('No object name provided for metadata/layout sync entry');
      return [{
        ...syncEntry,
        status: SYNC_ENTRY_STATUS.ERROR,
      }]
    }

    logger.veevaVaultSyncer.debug(`Getting ${objectName} layout/metadata...`);
    const { data, errors } = await this.appsyncClient.graphql({
      query: getVeevaVaultObjectMetadataLayouts,
      variables: {
        encryptedSessionId: this.getEncryptedSessionId(),
        objectName,
      },
    });

    if (errors || !data?.getVeevaVaultObjectMetadataLayouts) {
      return [{
        ...syncEntry,
        status: SYNC_ENTRY_STATUS.ERROR,
      }]
    }

    // THIS FUNCTION STRUCTURES THE LAYOUTS, AND ITERATES OVER THE FIELDS OF THEM TO DETERMINE, AND ADD, ADDITIONAL HANDLING //
    return this.handleObjectMetadataLayoutsResponse(syncEntry, data.getVeevaVaultObjectMetadataLayouts);
  }

  async getPicklists(syncEntry: SyncEntry): Promise<SyncEntry> {
    const fieldNames = syncEntry.parameters.fieldNames;

    if (!fieldNames) {
      logger.veevaVaultSyncer.debug(`No picklist items required for ${syncEntry.parameters.apiName}`);
      return {
        ...syncEntry,
        status: SYNC_ENTRY_STATUS.SYNCED,
      };
    }

    logger.veevaVaultSyncer.debug(`Getting ${syncEntry.parameters.apiName} picklists...`);
    const { data, errors } = await this.appsyncClient.graphql({
      query: getVeevaVaultPicklistsItems,
      variables: {
        encryptedSessionId: this.getEncryptedSessionId(),
        objectType: syncEntry.parameters.recordTypeId || '',
        fieldNames,
      },
    });

    if (errors || !data?.getVeevaVaultPicklistsItems) {
      return {
        ...syncEntry,
        status: SYNC_ENTRY_STATUS.ERROR,
      }
    }

    return {
      ...syncEntry,
      data: JSON.parse(data.getVeevaVaultPicklistsItems.picklists),
      status: SYNC_ENTRY_STATUS.SYNCED,
      lastSynced: new Date().getTime(),
    };
  }

  async getLookup(syncEntry: SyncEntry): Promise<SyncEntry> {
    const lookupApiName = syncEntry.parameters.lookupApiName;

    // THIS IS A SPECIFIC QUERY RATHER THAN A REGULAR LOOKUP //
    if (syncEntry.parameters.query && syncEntry.fetchType === SYNC_ENTRY_FETCH_TYPE.SOQL) {
      return this.handleSyncEntryQuery(syncEntry);
    }

    if (!lookupApiName) {
      logger.veevaVaultSyncer.debug(`No lookup item required for ${syncEntry.parameters.apiName}`);
      return {
        ...syncEntry,
        status: SYNC_ENTRY_STATUS.SYNCED,
      };
    }

    logger.veevaVaultSyncer.debug(`Getting ${syncEntry.parameters.lookupApiName} lookup...`);
    const { data, errors } = await this.appsyncClient.graphql({
      query: getVeevaVaultLookupItems,
      variables: {
        encryptedSessionId: this.getEncryptedSessionId(),
        lookup: lookupApiName,
      },
    });

    if (errors || !data?.getVeevaVaultLookupItems) {
      return {
        ...syncEntry,
        status: SYNC_ENTRY_STATUS.ERROR,
      }
    }

    return {
      ...syncEntry,
      data: JSON.parse(data.getVeevaVaultLookupItems.records),
      status: SYNC_ENTRY_STATUS.SYNCED,
      lastSynced: new Date().getTime(),
    };
  }

  async handleSyncEntryQuery(syncEntry: SyncEntry): Promise<SyncEntry> {
    logger.veevaVaultSyncer.debug('Handling SyncEntry query of ' +
      `${syncEntry.parameters.lookupApiName} for ${syncEntry.parameters.apiName}...`);

    if (!syncEntry.parameters.query) {
      logger.veevaVaultSyncer.warn('No query provided for SyncEntry...');
      return {
        ...syncEntry,
        status: SYNC_ENTRY_STATUS.SYNCED,
      };
    }

    let updatedEntry: SyncEntry = { ...syncEntry };

    let currentNextPage: string | undefined;
    let hasError = false;

    while (true) {
      const { data, errors } = await this.appsyncClient.graphql({
        query: getVeevaVaultQueryResults,
        variables: {
          encryptedSessionId: this.getEncryptedSessionId(),
          query: syncEntry.parameters.query,
          nextPage: currentNextPage,
        },
      });

      if (errors || !data?.getVeevaVaultQueryResults?.data) {
        logger.veevaVaultSyncer.error('Error fetching query results for SyncEntry...', {
          query: syncEntry.parameters.query,
          errors,
        });

        updatedEntry = {
          ...updatedEntry,
          status: SYNC_ENTRY_STATUS.ERROR,
        };
        hasError = true;
        break;
      }

      const response = JSON.parse(data.getVeevaVaultQueryResults.data);

      updatedEntry = {
        ...updatedEntry,
        data: updatedEntry.data ? [...updatedEntry.data, ...response] : response,
      };

      if (data.getVeevaVaultQueryResults.nextPage) {
        currentNextPage = data.getVeevaVaultQueryResults.nextPage;
      } else {
        break;
      }
    }

    if (!hasError) {
      updatedEntry = { ...updatedEntry, status: SYNC_ENTRY_STATUS.SYNCED };
    }

    return updatedEntry;
  }

  async queryRecordName(apiName: string, recordId: string): Promise<string | undefined> {
    try {
      const query = `SELECT id, name__v FROM ${apiName} WHERE id = '${recordId}'`;
      const { data, errors } = await this.appsyncClient.graphql({
        query: getVeevaVaultQueryResults,
        variables: {
          encryptedSessionId: this.getEncryptedSessionId(),
          query,
        },
      });

      if (errors || !data?.getVeevaVaultQueryResults?.data) {
        logger.veevaVaultSyncer.warn('Failed to query record name for', recordId);
        return undefined;
      }

      const records = JSON.parse(data.getVeevaVaultQueryResults.data);
      return records?.[0]?.name__v;
    } catch (e) {
      logger.veevaVaultSyncer.warn('Error querying record name:', e);
      return undefined;
    }
  }

  async deleteVeevaVaultObjects(
    objectAPIName: string,
    payloads: VeevaVaultObjectToDeletePayload[],
  ): Promise<CRMSubmitResponseReferences> {
    if (!payloads.length) {
      logger.veevaVaultSyncer.warn('No payloads received, skipping API call...');
      return {};
    }

    logger.veevaVaultSyncer.info(`Deleting VeevaVault [${objectAPIName}] objects`, payloads);

    const { data } = await this.appsyncClient.graphql({
      query: deleteVeevaVaultObjects,
      variables: {
        input: {
          encryptedSessionId: this.getEncryptedSessionId(),
          objectAPIName,
          ids: payloads.map((payload) => payload.externalId),
        },
      },
    });

    const response: VeevaVaultDeleteObjectsOutput[] = JSON.parse(data.deleteVeevaVaultObjects.response);
    return structureDeleteObjectsResponse(payloads, response);
  }

  async upsertVeevaVaultObjects(
    apiName: string,
    payloads: VeevaVaultUpsertObjectPayload[],
    idParam?: string): Promise<CRMSubmitResponseReferences> {
    if (!payloads.length) {
      logger.veevaVaultSyncer.info('No payloads received, skipping API call...');
      return {};
    }

    const CHUNK_SIZE = 25;
    const chunks: VeevaVaultUpsertObjectPayload[][] = [];
    for (let i = 0; i < payloads.length; i += CHUNK_SIZE) {
      chunks.push(payloads.slice(i, i + CHUNK_SIZE));
    }

    logger.veevaVaultSyncer.info(
      `Upserting VeevaVault [${apiName}] objects in ${chunks.length} chunk(s) of up to ${CHUNK_SIZE}`,
      payloads,
    );

    const chunkResults = await Promise.allSettled(
      chunks.map((chunk) =>
        this.appsyncClient.graphql({
          query: upsertVeevaVaultObjects,
          variables: {
            input: {
              encryptedSessionId: this.getEncryptedSessionId(),
              objectAPIName: apiName,
              objects: JSON.stringify(chunk.map(({ object }) => object)),
              idParam,
            },
          },
        }),
      ),
    );

    return chunkResults.reduce<CRMSubmitResponseReferences>((acc, result, index) => {
      const chunk = chunks[index];

      if (result.status === 'fulfilled') {
        const response: VeevaVaultUpsertObjectsOutput =
          JSON.parse(result.value.data.upsertVeevaVaultObjects.response);
        return { ...acc, ...structureUpsertResponse(chunk, response) };
      }

      logger.veevaVaultSyncer.error(
        `Failed to upsert VeevaVault [${apiName}] chunk ${index + 1}/${chunks.length}`,
        result.reason,
      );

      const chunkError = chunk.reduce<CRMSubmitResponseReferences>((chunkAcc, payload) => ({
        ...chunkAcc,
        [payload.referenceId]: {
          syncStatus: SYNC_STATUS.ERROR,
          errorMessages: getErrorMessagesFromFailedRequest(result.reason),
        } satisfies RecordSubmitResponse,
      }), {});

      return { ...acc, ...chunkError };
    }, {});
  }

  private handleObjectMetadataLayoutsResponse(syncEntry: SyncEntry,
    response: VeevaVaultObjectMetadataLayouts): SyncEntry[] {
    logger.veevaVaultSyncer.debug(`Handling ${syncEntry.parameters.apiName} layouts/metadata response...`);

    // 1. STRUCTURE THE LAYOUTS' RESPONSE CREATING ONE SYNC ENTRY PER LAYOUT
    const structuredLayouts = getStructureObjectLayouts(syncEntry, response);

    // 2. ITERATE OVER THE FIELDS OF THE LAYOUT TO DETERMINE IF THERE ARE FIELDS
    // THAT REQUIRE SPECIAL HANDLING OR NEW SYNCENTRIES CREATION (E.G. PICKLISTS, LOOKUPS, CONTROLS)
    const additionalEntries: SyncEntry[] = getAdditionalEntriesBasedOnLayouts(structuredLayouts);

    return [
      ...structuredLayouts,
      ...additionalEntries,
    ];
  }
}
